import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Translator from '%app/js/common/services/translator';
import {
    CATEGORY_MARKETING,
    CATEGORY_NECESSARY,
    CATEGORY_STATISTICS,
} from '%paysera_service_agreement/js/CookiesUserAgreement/config/constants';
import {
    getMarketingCookiesData,
    getNecessaryCookiesData,
    getStatisticsCookiesData,
} from '%paysera_service_agreement/js/CookiesUserAgreement/component/CookiesTable/tableData';

const CATEGORIES = [
    { key: CATEGORY_NECESSARY, getData: getNecessaryCookiesData },
    { key: CATEGORY_STATISTICS, getData: getStatisticsCookiesData },
    { key: CATEGORY_MARKETING, getData: getMarketingCookiesData },
];

const COLUMNS = ['name', 'provider', 'expiry', 'purpose'];

const CARD_FIELD_COLUMNS = ['provider', 'expiry', 'purpose'];

const PANEL_ID = 'redesign-cookie-category-panel';

const CONTAINER_SX = {
    width: '100%',
    mt: 2,
};

const TABS_SX = {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    borderBottom: 1,
    borderColor: 'divider',
};

const DESCRIPTION_SX = {
    mt: 2,
    mb: 1,
};

const HEADER_CELL_SX = {
    fontWeight: 'fontWeightMedium',
    whiteSpace: 'nowrap',
};

const CARD_SX = {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    p: 1.5,
    mb: 1.5,
};

const CARD_NAME_SX = {
    fontWeight: 'fontWeightMedium',
    mb: 1,
    wordBreak: 'break-word',
};

const CARD_FIELD_SX = {
    display: 'flex',
    gap: 1.5,
    mb: 0.5,
};

const CARD_LABEL_SX = {
    color: 'text.secondary',
    flexShrink: 0,
    minWidth: 72,
};

const CARD_VALUE_SX = {
    wordBreak: 'break-word',
};

const cookieRowPropType = PropTypes.objectOf(PropTypes.string).isRequired;

function CategoryCookieTable({ category, rows }) {
    return (
        <TableContainer>
            <Table size="small" data-testid={`redesign-cookie-table-${category}`}>
                <TableHead>
                    <TableRow>
                        {COLUMNS.map(column => (
                            <TableCell key={column} sx={HEADER_CELL_SX}>
                                {Translator.trans(`service_agreement_cookies.table.${column}`)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={`${category}-${row.name}`}>
                            {COLUMNS.map(column => (
                                <TableCell key={column}>
                                    <Typography variant="body2">{row[column]}</Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

CategoryCookieTable.propTypes = {
    category: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(cookieRowPropType).isRequired,
};

function CategoryCookieCards({ category, rows }) {
    return (
        <Box data-testid={`redesign-cookie-table-${category}`}>
            {rows.map(row => (
                <Box key={`${category}-${row.name}`} sx={CARD_SX} data-testid="redesign-cookie-card">
                    <Typography variant="body2" sx={CARD_NAME_SX}>{row.name}</Typography>
                    {CARD_FIELD_COLUMNS.map(column => (
                        <Box key={column} sx={CARD_FIELD_SX}>
                            <Typography variant="body2" sx={CARD_LABEL_SX}>
                                {Translator.trans(`service_agreement_cookies.table.${column}`)}
                            </Typography>
                            <Typography variant="body2" sx={CARD_VALUE_SX}>{row[column]}</Typography>
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
}

CategoryCookieCards.propTypes = {
    category: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(cookieRowPropType).isRequired,
};

function CookieConsentCategoryTables() {
    const [selectedCategory, setSelectedCategory] = useState(CATEGORY_NECESSARY);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleCategoryChange = (event, category) => {
        setSelectedCategory(category);
    };

    const activeCategory = CATEGORIES.find(category => category.key === selectedCategory);
    const rows = activeCategory.getData();

    return (
        <Box sx={CONTAINER_SX} data-testid="redesign-cookie-consent-category-tables">
            <Tabs
                value={selectedCategory}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
                sx={TABS_SX}
                aria-label={Translator.trans('service_agreement_cookies.button.customise')}
            >
                {CATEGORIES.map(category => (
                    <Tab
                        key={category.key}
                        value={category.key}
                        label={Translator.trans(`service_agreement_cookies.category.${category.key}`)}
                        id={`redesign-cookie-category-tab-${category.key}`}
                        aria-controls={PANEL_ID}
                    />
                ))}
            </Tabs>
            <Box
                role="tabpanel"
                id={PANEL_ID}
                aria-labelledby={`redesign-cookie-category-tab-${selectedCategory}`}
            >
                <Typography variant="body2" sx={DESCRIPTION_SX}>
                    {Translator.trans(`service_agreement_cookies.category.${selectedCategory}.body`)}
                </Typography>
                {isMobile
                    ? <CategoryCookieCards category={selectedCategory} rows={rows} />
                    : <CategoryCookieTable category={selectedCategory} rows={rows} />}
            </Box>
        </Box>
    );
}

export default React.memo(CookieConsentCategoryTables);
